/**
 * @fileoverview Loading-placeholder atom.
 * @description A pulsing gray block used to build skeleton screens — every feature's pending-query
 * state composes these (and the higher-level KpiStripSkeleton/TableSkeleton) into a shape that previews the real layout, instead of a bare "Loading…" line.
 * Width/height are plain style values rather than a variant prop, since every skeleton shape is a
 * one-off dictated by the real content it stands in for — there's no fixed set of sizes to name as
 * variants the way Button's or Badge's are.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface SkeletonProps {
  width?: string | number
  height?: string | number
  variant?: 'block' | 'circle'
}

/**
 * @component
 * @param {SkeletonProps} props - The placeholder's size and shape.
 * @param {string | number} [props.width] - CSS width (e.g. "100%", 120). Defaults to "100%".
 * @param {string | number} [props.height] - CSS height (e.g. 16). Defaults to 14.
 * @param {'block' | 'circle'} [props.variant] - "block" (default, rounded rectangle) or "circle"
 * (fully rounded, for avatar-shaped placeholders).
 */
function Skeleton({ width = '100%', height = 14, variant = 'block' }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      style={{ width, height }}
      className={`block animate-pulse bg-surface-muted ${variant === 'circle' ? 'rounded-full' : 'rounded-md'}`}
    />
  )
}

export default Skeleton
